// Pre-Checkout Address Validation System
// Validates shipping addresses BEFORE customers reach Shopify's checkout page
// Prevents customers from bypassing location restrictions

(function () {
  'use strict';

  // Prevent multiple initializations
  if (window.preCheckoutValidatorInitialized) {
    console.log('Pre-checkout validator already initialized, skipping...');
    return;
  }
  window.preCheckoutValidatorInitialized = true;

  // @ts-ignore - Suppress TypeScript errors for this JavaScript file

  // Constants
  const STORAGE_KEY = 'customer_delivery_tag';
  const STORAGE_HUMAN_KEY = 'customer_delivery_location';
  const VALIDATED_ADDRESS_KEY = 'validated_shipping_address';
  const CHECKOUT_ALLOWED_KEY = 'checkout_allowed';

  // Data URLs (matching location-filter.js)
  const INJECTED = (window && window.DELIVERY_ASSET_URLS) || {};
  // Fallback to empty string if not injected to prevent 404s
  const LOCATIONS_URL_PRIMARY = INJECTED.turkeyLocations || '';
  const LOCATIONS_URL_FALLBACK = INJECTED.turkeyFallback || '/assets/Turkey.json';

  // State
  let locationsData = null;
  let isInitializing = false;

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    if (isInitializing) return;
    isInitializing = true;
    
    console.log('Pre-checkout validator loaded');

    // Don't run on collection pages to prevent conflicts
    if (window.location.pathname.includes('/collections/') || 
        document.querySelector('.collection') || 
        document.querySelector('[data-collection]')) {
      console.log('Pre-checkout validator: Skipping collection page');
      return;
    }

    if (isCartPage()) {
      initCartValidation();
    }

    // Monitor location changes from other pages
    monitorLocationChanges();

    // Block direct checkout access
    blockDirectCheckoutAccess();

    // Check/Load independent location data
    loadLocations();
  }

  function isCartPage() {
    return window.location.pathname.includes('/cart') ||
      document.querySelector('.cart') ||
      document.querySelector('[data-cart]') ||
      document.querySelector('.cart-page') ||
      document.querySelector('.cart-drawer') ||
      document.querySelector('.cart-items');
  }

  // --- Data Loading ---
  async function loadLocations() {
    if (locationsData) return; // already loaded

    try {
      // Try simplified assets/turkey-locations.json first
      const resp = await fetch(LOCATIONS_URL_PRIMARY);
      if (resp.ok) {
        const data = await resp.json();
        if (Array.isArray(data)) {
          locationsData = { provinces: data.map(p => ({ name: p.name, districts: p.districts })) };
          return;
        }
      }
    } catch (e) {
      // ignore
    }

    try {
      // Fallback to Turkey.json
      const resp = await fetch(LOCATIONS_URL_FALLBACK);
      if (resp.ok) {
        const raw = await resp.json();
        if (Array.isArray(raw)) {
          locationsData = {
            provinces: raw.map(p => ({
              name: titleCaseTr(p.name),
              districts: Array.isArray(p.districts) ? p.districts.map(d => titleCaseTr(d.name)) : []
            }))
          };
        }
      }
    } catch (e) {
      console.error('Validator failed to load locations data', e);
      locationsData = { provinces: [] };
    }
  }

  function titleCaseTr(str) {
    if (!str) return '';
    const lower = str.toLocaleLowerCase('tr-TR');
    return lower.split(' ').map(w => w.charAt(0).toLocaleUpperCase('tr-TR') + w.slice(1)).join(' ');
  }

  function slugifyTr(input) {
    if (!input) return '';
    const map = { 'Ç': 'c', 'ç': 'c', 'Ğ': 'g', 'ğ': 'g', 'İ': 'i', 'I': 'i', 'ı': 'i', 'Ö': 'o', 'ö': 'o', 'Ş': 's', 'ş': 's', 'Ü': 'u', 'ü': 'u' };
    return String(input).trim()
      .replace(/[ÇçĞğİIıÖöŞşÜü]/g, m => map[m] || m)
      .normalize('NFKD')
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .toLowerCase();
  }

  function initCartValidation() {
    // Always show validator and check current state
    const restrictedItems = getLocationRestrictedItems();

    // Don't show the pre-checkout validator UI anymore - use the original location selector instead
    // Just handle the validation logic
    updateCheckoutState(restrictedItems);

    // Monitor for cart changes
    monitorCartChanges();
  }

  function updateCheckoutState(restrictedItems) {
    const currentLocation = localStorage.getItem(STORAGE_HUMAN_KEY);
    const currentTag = localStorage.getItem(STORAGE_KEY);

    if (!currentLocation && !currentTag) {
      showValidationMessage('📍 Lütfen teslimat bölgenizi seçin.', 'warning');
      localStorage.removeItem(CHECKOUT_ALLOWED_KEY);
      document.dispatchEvent(new CustomEvent('validation:changed'));
      return;
    }

    if (restrictedItems.length === 0) {
      // No restricted items - allow checkout
      showCheckoutButtons();
      showValidationMessage('✅ Teslimat uygun.', 'success');
      localStorage.setItem(CHECKOUT_ALLOWED_KEY, 'true');
      return;
    }

    // Check if current location is valid for all restricted items
    validateCurrentLocation(restrictedItems);
  }

  function getLocationRestrictedItems() {
    const items = [];
    const currentLocation = localStorage.getItem(STORAGE_HUMAN_KEY);

    // Get items from cart
    const cartItemSelectors = ['.cart-item', '.line-item', '[data-line-item]', '.cart-drawer__item', '.cart__item'];
    let cartItems = [];
    for (const selector of cartItemSelectors) {
      const found = document.querySelectorAll(selector);
      if (found.length > 0) {
        cartItems = found;
        break;
      }
    }

    if (cartItems.length === 0) {
      if (currentLocation) {
        // Fallback item
        return [{ productId: 'unknown', productName: 'Sepetteki Ürünler', zones: [], tags: [] }];
      }
      return items;
    }

    cartItems.forEach(item => {
      const zones = item.getAttribute('data-zones') || '';
      const tags = item.getAttribute('data-tags') || '';
      // If location is selected, treat all items as potentially restricted
      if (currentLocation) {
        items.push({
          productId: item.getAttribute('data-product-id') || 'unknown',
          productName: 'Product', // Name parsing simplified for brevity
          zones: zones.split(',').map(z => z.trim()).filter(Boolean),
          tags: tags.split(',').map(t => t.trim()).filter(Boolean)
        });
      }
    });

    return items;
  }

  function hideCheckoutButtons() {
    // Set explicit 'false' so cart-summary blocks checkout (null/missing = allowed by default)
    localStorage.setItem(CHECKOUT_ALLOWED_KEY, 'false');
    document.dispatchEvent(new CustomEvent('validation:changed'));
  }

  function showCheckoutButtons() {
    localStorage.setItem(CHECKOUT_ALLOWED_KEY, 'true');
    document.dispatchEvent(new CustomEvent('validation:changed'));
  }

  function addAddressValidationForm(restrictedItems) {
    const cartContainer = document.querySelector('.cart, .cart-page, .cart-drawer__content, [data-cart]');
    if (!cartContainer) return;

    // Eğer zaten varsa, kaldır
    const existingForm = document.getElementById('pre-checkout-validation');
    if (existingForm) {
      existingForm.remove();
    }

    const validationForm = document.createElement('div');
    validationForm.id = 'pre-checkout-validation';
    validationForm.className = 'pre-checkout-validation';

    const currentLocation = localStorage.getItem(STORAGE_HUMAN_KEY);
    const locationDisplay = currentLocation || 'Bölge seçilmedi';

    validationForm.innerHTML = `
      <!-- View Mode -->
      <div class="validation-view-mode" id="validator-view-mode">
        <div class="location-status">
          <span class="location-text">
            <strong id="validator-current-display">${locationDisplay}</strong>
            <span class="status-note" id="validation-message-inline">Kontrol ediliyor...</span>
          </span>
        </div>
        <button type="button" class="validator-edit-btn" id="validator-edit-btn" title="Düzenle">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
        </button>
      </div>

      <!-- Edit Mode -->
      <div class="validation-edit-mode" id="validator-edit-mode" style="display:none;">
        <div class="validator-inputs">
           <select id="validator-province" class="validator-select">
             <option value="">İl</option>
           </select>
           <select id="validator-district" class="validator-select" disabled>
             <option value="">İlçe</option>
           </select>
        </div>
        <div class="validator-actions">
           <button type="button" class="validator-save-btn" id="validator-save-btn">✓</button>
           <button type="button" class="validator-cancel-btn" id="validator-cancel-btn">✕</button>
        </div>
      </div>
    `;

    // Add styles
    const styles = `
      <style>
        .pre-checkout-validation {
          background: #fff3cd;
          border: 1px solid #ffeaa7;
          border-radius: 6px;
          padding: 8px 12px;
          margin: 8px 0;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 13px;
          max-width: 600px; /* Konteyner genişliğini sınırla */
          min-height: 60px; /* Pre-allocate space to prevent layout shift */
          transition: none; /* Remove transitions that cause glitch */
        }
        
        .validation-view-mode {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
        }
        
        .location-status {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
          min-width: 0;
        }
        
        .status-icon { 
          font-size: 16px; 
          flex-shrink: 0;
        }
        
        .location-text {
          display: flex;
          flex-direction: column;
          line-height: 1.2;
          overflow: hidden;
          flex: 1;
          min-width: 0;
        }
        
        .location-text strong {
          font-weight: 600;
          color: #333;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .status-note {
          font-size: 11px;
          color: #856404;
          white-space: nowrap; /* Tek satırda tut */
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .validator-edit-btn, .validator-save-btn, .validator-cancel-btn {
          background: transparent;
          border: 1px solid rgba(0,0,0,0.1);
          border-radius: 4px;
          cursor: pointer;
          min-width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          flex-shrink: 0;
        }
        .validator-edit-btn:hover, .validator-cancel-btn:hover { background: rgba(0,0,0,0.05); }
        .validator-save-btn { background: #333; color: #fff; border-color: #333; }
        .validator-save-btn:hover { background: #000; }
        
        /* Edit Mode Styles */
        .validation-edit-mode {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          align-items: center;
        }
        
        .validator-inputs {
          display: flex;
          gap: 6px;
          flex: 1;
          min-width: 200px; /* Minimum genişlik */
        }
        
        .validator-select {
          flex: 1;
          padding: 4px 8px;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 13px;
          max-width: 120px;
          min-width: 80px;
        }
        
        .validator-actions {
          display: flex;
          gap: 4px;
          flex-shrink: 0;
          align-items: center;
          justify-content: center;
        }
        
        .status-note.success { color: #155724; }
        .status-note.error { color: #721c24; }
        .status-note.warning { color: #856404; }
        
        /* Desktop optimizasyonu */
        @media (min-width: 768px) {
          .pre-checkout-validation {
            max-width: 500px; /* Desktop'ta daha kompakt */
          }
          
          .location-text {
            flex-direction: row; /* Desktop'ta yan yana */
            align-items: center;
            gap: 8px;
          }
          
          .status-note {
            flex-shrink: 0; /* Desktop'ta sıkışmasın */
            white-space: nowrap;
          }
          
          .validator-inputs {
            min-width: 250px; /* Desktop'ta biraz daha geniş */
          }
        }
      </style>
    `;

    // Insert form
    const checkoutSection = cartContainer.querySelector('.cart-footer, .cart-actions, .cart-summary') || cartContainer;
    if (!document.querySelector('#pre-checkout-validator_styles')) {
      const styleEl = document.createElement('div');
      styleEl.id = 'pre-checkout-validator_styles';
      styleEl.innerHTML = styles;
      checkoutSection.parentNode.insertBefore(styleEl, checkoutSection);
    }

    // Remove if previously added just to be safe
    const old = document.getElementById('pre-checkout-validation');
    if (old) old.remove();

    checkoutSection.insertAdjacentElement('beforebegin', validationForm);

    // Bind Events
    bindValidatorEvents(validationForm);
  }

  function bindValidatorEvents(form) {
    const viewMode = form.querySelector('#validator-view-mode');
    const editMode = form.querySelector('#validator-edit-mode');
    const editBtn = form.querySelector('#validator-edit-btn');
    const cancelBtn = form.querySelector('#validator-cancel-btn');
    const saveBtn = form.querySelector('#validator-save-btn');
    const provinceSel = form.querySelector('#validator-province');
    const districtSel = form.querySelector('#validator-district');

    // Toggle Edit
    editBtn.addEventListener('click', async () => {
      viewMode.style.display = 'none';
      editMode.style.display = 'flex';
      await populateValidatorProvinces(provinceSel);

      // Preselect current if exists
      const current = localStorage.getItem(STORAGE_HUMAN_KEY);
      if (current && current.includes('/')) {
        const [prov, dist] = current.split('/').map(s => s.trim());
        if (prov) {
          provinceSel.value = prov;
          handleValidatorProvinceChange(provinceSel, districtSel, dist);
        }
      }
    });

    // Cancel
    cancelBtn.addEventListener('click', () => {
      editMode.style.display = 'none';
      viewMode.style.display = 'flex';
    });

    // Province Change
    provinceSel.addEventListener('change', () => {
      handleValidatorProvinceChange(provinceSel, districtSel);
    });

    // Save
    saveBtn.addEventListener('click', () => {
      const prov = provinceSel.value;
      const dist = districtSel.value;

      if (!prov || !dist) {
        alert('Lütfen il ve ilçe seçiniz');
        return;
      }

      const tag = `delivery-${slugifyTr(prov)}-${slugifyTr(dist)}`;
      const human = `${prov} / ${dist}`;

      localStorage.setItem(STORAGE_KEY, tag);
      localStorage.setItem(STORAGE_HUMAN_KEY, human);

      // Update UI immediate
      document.getElementById('validator-current-display').textContent = human;

      // Dispatch event
      const evt = new CustomEvent('location:changed', {
        detail: { province: prov, district: dist, tag: tag, humanReadable: human }
      });
      document.dispatchEvent(evt);
      window.dispatchEvent(new Event('storage')); // trigger cross-tab listeners

      // Return to view mode
      editMode.style.display = 'none';
      viewMode.style.display = 'flex';

      // Re-validate
      initCartValidation();
    });
  }

  async function populateValidatorProvinces(select) {
    if (!locationsData) await loadLocations();
    if (!locationsData || !locationsData.provinces) return;

    select.innerHTML = '<option value="">İl</option>';
    locationsData.provinces
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, 'tr-TR'))
      .forEach(p => {
        const opt = document.createElement('option');
        opt.value = p.name;
        opt.textContent = p.name;
        select.appendChild(opt);
      });
  }

  function handleValidatorProvinceChange(provSel, distSel, preselectDist = null) {
    const selected = provSel.value;
    distSel.innerHTML = '<option value="">İlçe</option>';
    distSel.disabled = true;

    if (!locationsData) return;

    const provData = locationsData.provinces.find(p => p.name === selected);
    if (!provData) return;

    provData.districts
      .slice()
      .sort((a, b) => a.localeCompare(b, 'tr-TR'))
      .forEach(d => {
        const opt = document.createElement('option');
        opt.value = d;
        opt.textContent = d;
        distSel.appendChild(opt);
      });

    distSel.disabled = false;
    if (preselectDist) distSel.value = preselectDist;
  }

  async function validateAddressAgainstItems(province, city, restrictedItems) {
    if (restrictedItems.length === 0) return true;

    // Try to get zonesMap
    let zonesMap = window.zonesMap || {};
    if (Object.keys(zonesMap).length === 0) {
      try {
        const resp = await fetch(INJECTED.zones || '/assets/delivery-zones.json');
        if (resp.ok) zonesMap = await resp.json();
        window.zonesMap = zonesMap;
      } catch (e) { }
    }

    const inputLocation = `${province} / ${city}`;

    for (const item of restrictedItems) {
      if (!item.zones || item.zones.length === 0 && (!item.tags || item.tags.length === 0)) continue;

      let supported = [];
      // From zones
      item.zones.forEach(z => {
        const locs = zonesMap[z] || [];
        supported = supported.concat(locs);
      });
      // From tags
      item.tags.forEach(t => {
        if (t.startsWith('zone-')) {
          const locs = zonesMap[t] || [];
          supported = supported.concat(locs);
        }
      });

      if (supported.length === 0) continue; // no specific restrictions found for this tag/zone

      // Check match
      const isMatch = supported.some(tag => {
        // simple check: tag contains input slug
        const tagSlug = tag.replace('delivery-', '');
        const inputSlug = `${slugifyTr(province)}-${slugifyTr(city)}`;
        return tagSlug === inputSlug;
      });

      if (!isMatch) return false;
    }
    return true;
  }

  async function validateCurrentLocation(restrictedItems) {
    const currentLocation = localStorage.getItem(STORAGE_HUMAN_KEY);

    if (!currentLocation) {
      hideCheckoutButtons();
      showValidationMessage('📍 Lütfen teslimat bölgenizi seçin.', 'warning');
      return;
    }

    const parts = currentLocation.split(' / ');
    if (parts.length < 2) return;

    const [province, city] = parts;
    const isValid = await validateAddressAgainstItems(province, city, restrictedItems);

    if (isValid) {
      showCheckoutButtons();
      showValidationMessage('✅ Teslimat uygun.', 'success');
      localStorage.setItem(CHECKOUT_ALLOWED_KEY, 'true');
      localStorage.setItem(VALIDATED_ADDRESS_KEY, JSON.stringify({ province, city, timestamp: Date.now() }));
    } else {
      showValidationMessage('❌ Teslimat yapılamıyor.', 'error');
      localStorage.setItem(CHECKOUT_ALLOWED_KEY, 'false');
      localStorage.removeItem(VALIDATED_ADDRESS_KEY);
      document.dispatchEvent(new CustomEvent('validation:changed'));
    }
  }

  function showValidationMessage(message, type) {
    const messageSpan = document.getElementById('validation-message-inline');
    if (messageSpan) {
      messageSpan.textContent = message;
      messageSpan.className = `status-note ${type === 'success' ? 'success' : type === 'error' ? 'error' : 'warning'}`;
    }

    // Prevent layout shift by pre-allocating space
    const validationForm = document.getElementById('pre-checkout-validation');
    if (validationForm && !validationForm.style.minHeight) {
      validationForm.style.minHeight = '60px';
      validationForm.style.transition = 'none'; // Remove transitions that cause glitch
    }

    // Checkout button'ı güncelle
    if (typeof window.updateCheckoutButton === 'function') {
      setTimeout(window.updateCheckoutButton, 100);
    }

    // Custom event dispatch et
    const validationEvent = new CustomEvent('validation:changed', {
      detail: { message, type, isValid: type === 'success' }
    });
    document.dispatchEvent(validationEvent);
  }

  function updateValidationUI() {
    // Re-run validation logic
    initCartValidation();
  }

  function monitorLocationChanges() {
    window.addEventListener('storage', function (e) {
      if (e.key === STORAGE_HUMAN_KEY || e.key === STORAGE_KEY) {
        if (isCartPage()) {
          setTimeout(() => {
            initCartValidation();
          }, 100);
        }
      }
    });

    document.addEventListener('location:changed', function (e) {
      if (isCartPage()) {
        setTimeout(() => {
          initCartValidation();
        }, 100);
      }
    });

    // Also listen for validation changes to update UI
    document.addEventListener('validation:changed', function (e) {
      if (isCartPage()) {
        setTimeout(() => {
          const restrictedItems = getLocationRestrictedItems();
          updateCheckoutState(restrictedItems);
        }, 100);
      }
    });
  }

  function blockDirectCheckoutAccess() {
    if (window.location.pathname.includes('/checkout')) {
      const allowed = localStorage.getItem(CHECKOUT_ALLOWED_KEY);
      if (!allowed) window.location.href = '/cart';
    }
  }

  let _monitorInit = false;
  function monitorCartChanges() {
    if (_monitorInit) return;
    _monitorInit = true;
    document.addEventListener('cart:updated', () => {
      setTimeout(() => { initCartValidation(); }, 200);
    });
  }

})();
